import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  Box,
  TablePagination,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";

type SortDirection = "asc" | "desc";

interface SortConfig {
  key: string;
  direction: SortDirection;
}

interface Column {
  key: string;
  header: string;
  render?: (value: any, rowData: any) => React.ReactNode;
  detailsOnly?: boolean;
  sortable?: boolean;
  align?: "left" | "center" | "right";
}

interface DynamicTableProps {
  data: any[];
  columns: Column[];
  sections?: { label: string; value: string }[];
  onSectionChange?: (value: string) => void;
  onRowClick?: (row: any) => void;
  rowsPerPageOptions?: number[];
  defaultRowsPerPage?: number;
}

const CustomTableSortLabel = styled(TableSortLabel)(({}) => ({
  "& .MuiTableSortLabel-icon": {
    visibility: "hidden",
  },
  "&:hover .MuiTableSortLabel-icon": {
    visibility: "visible",
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  "& .MuiTypography-root": {
    color: theme.palette.text.primary,
  },
}));

const DynamicTable: React.FC<DynamicTableProps> = ({
  data,
  columns,
  sections,
  onSectionChange,
  onRowClick,
  rowsPerPageOptions = [5, 10, 25],
  defaultRowsPerPage = 5,
}) => {
  const [activeSection, setActiveSection] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeSection = (
    _event: React.SyntheticEvent,
    newValue: number
  ) => {
    setActiveSection(newValue);
    if (onSectionChange && sections) {
      onSectionChange(sections[newValue].value);
    }
  };

  const handleSort = (columnKey: string) => {
    setSortConfig((prevSort) => {
      if (prevSort?.key === columnKey) {
        return prevSort.direction === "asc"
          ? { key: columnKey, direction: "desc" }
          : null;
      }
      return { key: columnKey, direction: "asc" };
    });
  };

  const tableColumns = columns.filter((column) => !column.detailsOnly);

  const displayData = useMemo(() => {
    let sortedData = [...data];

    if (sortConfig) {
      sortedData.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (aValue == null) return sortConfig.direction === "asc" ? -1 : 1;
        if (bValue == null) return sortConfig.direction === "asc" ? 1 : -1;

        if (typeof aValue === "number" && typeof bValue === "number") {
          return sortConfig.direction === "asc"
            ? aValue - bValue
            : bValue - aValue;
        }

        const aString = String(aValue).toLowerCase();
        const bString = String(bValue).toLowerCase();

        return sortConfig.direction === "asc"
          ? aString.localeCompare(bString)
          : bString.localeCompare(aString);
      });
    }

    return sortedData.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [data, page, rowsPerPage, sortConfig]);

  return (
    <TableContainer component={Paper}>
      {sections && (
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={activeSection}
            onChange={handleChangeSection}
            aria-label="table sections"
          >
            {sections.map((section) => (
              <Tab
                key={section.value}
                label={
                  <Typography variant="h6" color="text.primary">
                    {section.label}
                  </Typography>
                }
              />
            ))}
          </Tabs>
        </Box>
      )}
      <Table>
        <TableHead>
          <TableRow>
            {tableColumns.map((column) => (
              <StyledTableCell key={column.key} align={column.align || "left"}>
                {column.sortable !== false ? (
                  <CustomTableSortLabel
                    active={sortConfig?.key === column.key}
                    direction={
                      sortConfig?.key === column.key
                        ? sortConfig.direction
                        : "asc"
                    }
                    onClick={() => handleSort(column.key)}
                  >
                    <Typography variant="body2" color="text.primary">
                      {column.header}
                    </Typography>
                  </CustomTableSortLabel>
                ) : (
                  <Typography variant="body2" color="text.primary">
                    {column.header}
                  </Typography>
                )}
              </StyledTableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {displayData.map((row, rowIndex) => (
            <TableRow
              key={rowIndex}
              onClick={() => onRowClick && onRowClick(row)}
              sx={{
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.action.hover,
                  cursor: "pointer",
                },
              }}
            >
              {tableColumns.map((column) => (
                <StyledTableCell
                  key={`${rowIndex}-${column.key}`}
                  align={column.align || "left"}
                >
                  <Typography variant="body2" color="text.primary">
                    {column.render
                      ? column.render(row[column.key], row)
                      : row[column.key]}
                  </Typography>
                </StyledTableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </TableContainer>
  );
};

export default DynamicTable;
